// SECURITY: Property data extraction and merging utilities for DealLens enrichment
// ASSUMPTION: Property data follows the shared extraction patterns used across deal/property flows
// RISK: Overwriting deal data with "Unknown" values from the property table is mitigated by selective merging

import { FALLBACK_ADDRESS } from "@/components/PropertyHeaderCard/constants";

/**
 * Sanitizes property ID by removing property# prefix for batch queries.
 * SECURITY: Prevents query failures from inconsistent ID formats.
 * 
 * @param {string|*} id - Property ID to sanitize
 * @returns {string|*} Sanitized property ID
 */
export const sanitizePropertyIdForBatch = (id) => {
  if (typeof id !== "string") return id;
  try {
    return id.replace(/^property#?/i, "");
  } catch {
    return id;
  }
};

/**
 * Extracts property display data from property details with fallbacks.
 * Aligns with propertyDataExtraction-style field resolution for deal rows
 * 
 * @param {Object|null} propertyDetails - Property details object
 * @returns {Object} Extracted and sanitized property display data
 */
export const extractPropertyDisplayData = (propertyDetails) => {
  if (!propertyDetails) {
    return {
      city: "Unknown City",
      state: "Unknown State",
      zipcode: "",
      county: "Unknown",
      address: FALLBACK_ADDRESS,
      hidden_address: false,
      sqft: 0,
      beds: 0,
      baths: 0,
      yearBuilt: "Unknown",
      annualPropertyTax: null,
    };
  }

  const attributes = propertyDetails.additional_attributes || {};

  const toTitleCase = (str) => {
    if (!str || typeof str !== "string") return str;
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const safeParseInt = (value) => {
    const parsed = parseInt(value);
    return isNaN(parsed) ? 0 : Math.max(0, parsed);
  };

  const safeParseFloat = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : Math.max(0, parsed);
  };

  const extractYearBuilt = () => {
    const metadataYear = propertyDetails.metadata?.original_data?.yearBuilt;
    const attributesYear = attributes.year_built || attributes.yearBuilt;
    const topLevelYear = propertyDetails.yearBuilt;
    const yearValue = metadataYear || attributesYear || topLevelYear;

    if (!yearValue || yearValue === "Unknown") return "Unknown";
    const yearInt = parseInt(yearValue);
    const currentYear = new Date().getFullYear();
    if (isNaN(yearInt) || yearInt < 1800 || yearInt > currentYear + 5) {
      return "Unknown";
    }
    return yearInt;
  };

  const extractAnnualPropertyTax = () => {
    const taxValue = attributes.annual_property_tax;
    const parsed = parseFloat(taxValue);
    return isNaN(parsed) || parsed < 0 ? null : parsed;
  };

  return {
    city: propertyDetails.city || "Unknown City",
    state: propertyDetails.state || "Unknown State",
    zipcode: propertyDetails.zipcode || "",
    county: toTitleCase(propertyDetails.county) || "Unknown",
    address: propertyDetails.full_address || propertyDetails.address || FALLBACK_ADDRESS,
    hidden_address: Boolean(propertyDetails.hidden_address),
    sqft: safeParseInt(attributes.square_feet || attributes.sqft || propertyDetails.sqft),
    beds: safeParseInt(attributes.bedrooms || attributes.beds || propertyDetails.beds),
    baths: safeParseFloat(attributes.bathrooms || attributes.baths || propertyDetails.baths),
    yearBuilt: extractYearBuilt(),
    annualPropertyTax: extractAnnualPropertyTax(),
  };
};

/**
 * Merges a DealLens deal with property details using a fallback strategy.
 * SECURITY: Only overwrites DealLens fields if property details provide non-default values.
 * 
 * @param {Object} deal - Normalized DealLens row
 * @param {Object|null} propertyDetails - Property details from batch fetch
 * @returns {Object} Enriched deal object
 */
export function mergeDealWithProperty(deal, propertyDetails) {
  if (!propertyDetails) return { ...deal, isEnriched: false };

  const displayData = extractPropertyDisplayData(propertyDetails);

  // Map plural to singular (DealLens contract uses bed/bath)
  // Map camelCase to snake_case (DealLens contract uses year_built)
  return {
    ...deal,
    full_address:
      displayData.address && displayData.address !== FALLBACK_ADDRESS ? displayData.address : deal.full_address,
    city: displayData.city && displayData.city !== "Unknown City" ? displayData.city : deal.city,
    state: displayData.state && displayData.state !== "Unknown State" ? displayData.state : deal.state,
    zipcode: displayData.zipcode || deal.zipcode,
    county: displayData.county && displayData.county !== "Unknown" ? displayData.county : deal.county,
    bed: displayData.beds > 0 ? displayData.beds : deal.bed,
    bath: displayData.baths > 0 ? displayData.baths : deal.bath,
    sqft: displayData.sqft > 0 ? displayData.sqft : deal.sqft,
    year_built: displayData.yearBuilt !== "Unknown" ? displayData.yearBuilt : deal.year_built,
    hidden_address: displayData.hidden_address ?? deal.hidden_address,
    annual_property_tax: displayData.annualPropertyTax ?? deal.annual_property_tax ?? null,
    propertyDetails, // Store for future reference
    isEnriched: true,
  };
}

/**
 * True when Phase 2 property hydration has finished (hit or miss).
 * Raw Phase 1 rows omit both flags — list maps should wait to avoid a second Static Maps fetch.
 *
 * @param {object|null|undefined} deal
 * @returns {boolean}
 */
export function isDealPropertyHydrated(deal) {
  return Boolean(deal?.propertyDetails) || deal?.isEnriched === true || deal?.isEnriched === false;
}

/** True when Phase 2 attached property rows (not a hydration miss). */
function hasUsablePropertyEnrichment(deal) {
  return Boolean(deal?.propertyDetails) || deal?.isEnriched === true;
}

/**
 * Keeps Phase 2 property fields when a fresher raw list/tail row would replace an enriched row.
 *
 * @param {object | null | undefined} incoming
 * @param {object | null | undefined} donor
 * @returns {object | null | undefined}
 */
export function carryForwardDealEnrichment(incoming, donor) {
  if (!incoming) return donor ?? incoming;
  if (!donor || !hasUsablePropertyEnrichment(donor)) return incoming;
  if (hasUsablePropertyEnrichment(incoming)) return incoming;

  return {
    ...incoming,
    full_address: incoming.full_address || donor.full_address,
    city: incoming.city || donor.city,
    state: incoming.state || donor.state,
    zipcode: incoming.zipcode || donor.zipcode,
    county: incoming.county || donor.county,
    bed: incoming.bed ?? donor.bed,
    bath: incoming.bath ?? donor.bath,
    sqft: incoming.sqft ?? donor.sqft,
    year_built: incoming.year_built ?? donor.year_built,
    hidden_address: incoming.hidden_address ?? donor.hidden_address,
    annual_property_tax: incoming.annual_property_tax ?? donor.annual_property_tax,
    propertyDetails: donor.propertyDetails,
    isEnriched: donor.isEnriched,
  };
}
